/**
 * Pure vector SVG exporter for Paper2Ink.
 * Spec reference: .hermes/plans/2026-09-16_195645-paper2ink-saas-spec.md (§5.5, Invariant 16, SAAS-216).
 *
 * Strict Security Rules:
 * - Allowlist: <svg>, <g>, <path>, <rect> ONLY.
 * - Absolutely NO <image>, <script>, <foreignObject>, <use>, external CSS, or event handlers.
 * - Default transparent background; white background via explicit vector <rect>.
 * - Bound to document revision ID.
 */

import { validateCIMDocument, type CIMDocument, type CompoundFill, type CenterlineStroke } from '../editor/model.ts';

const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function escapeXml(unsafe: string): string {
  return unsafe.replace(/[<>&'"]/g, (c) => {
    switch (c) {
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '&': return '&amp;';
      case '\'': return '&apos;';
      case '"': return '&quot;';
      default: return c;
    }
  });
}

function formatNum(v: number): string {
  if (Math.abs(v - Math.round(v)) < 1e-5) {
    return Math.round(v).toString();
  }
  return Number(v.toFixed(4)).toString();
}

function isIdentityMatrix(m: [number, number, number, number, number, number]): boolean {
  return (
    Math.abs(m[0] - 1) < 1e-6 &&
    Math.abs(m[1]) < 1e-6 &&
    Math.abs(m[2]) < 1e-6 &&
    Math.abs(m[3] - 1) < 1e-6 &&
    Math.abs(m[4]) < 1e-6 &&
    Math.abs(m[5]) < 1e-6
  );
}

export interface SVGExportOptions {
  background?: 'transparent' | 'white';
}

export function exportToSVG(doc: CIMDocument, options?: SVGExportOptions): string {
  validateCIMDocument(doc);

  const background = options?.background ?? 'transparent';
  if (background !== 'transparent' && background !== 'white') {
    throw new Error(`Invalid background option '${background}', expected 'transparent' or 'white'`);
  }

  const w = doc.page.width;
  const h = doc.page.height;
  const revId = escapeXml(doc.revision_id);
  const elements: string[] = [];

  if (background === 'white') {
    elements.push(`<rect width="${w}" height="${h}" fill="#ffffff"/>`);
  }

  for (const obj of doc.objects) {
    const objId = escapeXml(obj.id);
    const color = obj.color.trim();
    if (!HEX_COLOR_RE.test(color)) {
      throw new Error(`Invalid hex color format in object '${objId}': ${color}`);
    }

    let transformAttr = '';
    if (obj.transform && !isIdentityMatrix(obj.transform)) {
      const matrixStr = obj.transform.map(formatNum).join(' ');
      transformAttr = ` transform="matrix(${matrixStr})"`;
    }

    if (obj.kind === 'compound_fill') {
      const fillObj = obj as CompoundFill;
      const cmdParts: string[] = [];

      for (const ring of fillObj.rings) {
        if (!ring.points || ring.points.length === 0) continue;
        cmdParts.push(`M ${formatNum(ring.points[0][0])},${formatNum(ring.points[0][1])}`);
        for (let i = 1; i < ring.points.length; i++) {
          cmdParts.push(`L ${formatNum(ring.points[i][0])},${formatNum(ring.points[i][1])}`);
        }
        cmdParts.push('Z');
      }

      const dStr = cmdParts.join(' ');
      const fillRule = fillObj.fill_rule;
      const opacity = fillObj.opacity ?? 1.0;
      const opacityAttr = opacity < 1.0 ? ` fill-opacity="${formatNum(opacity)}"` : '';

      elements.push(
        `<path id="${objId}" d="${dStr}" fill="${color}" fill-rule="${fillRule}"${opacityAttr}${transformAttr}/>`
      );
    } else if (obj.kind === 'centerline_stroke') {
      const strokeObj = obj as CenterlineStroke;
      const cmdParts: string[] = [];
      const pts = strokeObj.points;
      if (pts.length > 0) {
        cmdParts.push(`M ${formatNum(pts[0][0])},${formatNum(pts[0][1])}`);
        for (let i = 1; i < pts.length; i++) {
          cmdParts.push(`L ${formatNum(pts[i][0])},${formatNum(pts[i][1])}`);
        }
      }

      const dStr = cmdParts.join(' ');
      const width = strokeObj.width ?? 1.0;
      const opacity = strokeObj.opacity ?? 1.0;
      const opacityAttr = opacity < 1.0 ? ` stroke-opacity="${formatNum(opacity)}"` : '';

      elements.push(
        `<path id="${objId}" d="${dStr}" fill="none" stroke="${color}" stroke-width="${formatNum(width)}" stroke-linecap="round" stroke-linejoin="round"${opacityAttr}${transformAttr}/>`
      );
    }
  }

  const body = elements.join('\n  ');
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" shape-rendering="crispEdges" version="1.1" data-revision-id="${revId}">`,
    `  <!-- paper2ink revision: ${revId}, document_id: ${escapeXml(doc.document_id)} -->`,
    `  ${body}`,
    '</svg>',
  ].join('\n');
}
